import {HttpErrorResponse} from '@angular/common/http'

import {
  throwError as observableThrowError,
  Observable,
  Subject,
  BehaviorSubject,
  Subscription,
} from 'rxjs'
import {catchError} from 'rxjs/operators'

import {NotificationService} from '@app/core/services/notification.service'
import {LoggerService} from '@app/core/services/logger.service'

import {SearchFilter} from '@app/core/services/search/search-filter.interface'

export interface LoadRequest {
  domain: string
  offset: number
  count: number
}

export interface LoadResult<T> {
  total: number
  items: T[]
}

export abstract class GenericListService<T> {
  filter: SearchFilter = null

  private loadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false)
  private totalSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0)
  private errorSubject: BehaviorSubject<Error> = new BehaviorSubject<Error>(
    null,
  )

  private loaded: T[] = []
  private listSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>(
    this.loaded,
  )

  public readonly loading: Observable<boolean> =
    this.loadingSubject.asObservable()
  public readonly total: Observable<number> = this.totalSubject.asObservable()
  public readonly error: Observable<Error> = this.errorSubject.asObservable()
  public readonly list: Observable<T[]> = this.listSubject.asObservable()

  private subscription: Subscription
  private activeDomain: string | null

  constructor(
    protected notify: NotificationService,
    protected logger: LoggerService,
  ) {}

  loadNext(count: number): Subject<T[]> {
    if (this.subscription) {
      this.logger.log('cancelling running load')
      this.subscription.unsubscribe()
    }
    this.load({
      count: count,
      offset: this.loaded.length,
      domain: this.activeDomain,
    })
    return this.listSubject
  }

  load(request: LoadRequest): Subject<T[]> {
    this.loadingSubject.next(true)

    this.subscription = this.retrieve(request)
      .pipe(catchError((err: Error) => this.handleError(err)))
      .subscribe(
        (result: LoadResult<T>) => {
          // process the results
          this.addLoadedItems(request.offset, result.items)
          // emit the new result list
          this.listSubject.next(this.loaded)

          // update the total count
          this.totalSubject.next(result.total)

          // clear the error, and loading subjects
          this.errorSubject.next(null)
          this.loadingSubject.next(false)
          this.subscription = null
        },
        (err: Error) => {
          // set the error and clear loading subject
          this.errorSubject.next(err)
          this.loadingSubject.next(false)
          this.loaded = []

          this.listSubject.next(this.loaded)
          this.totalSubject.next(0)
        },
      )

    return this.listSubject
  }

  clear(): void {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.loadingSubject.next(false)
    this.errorSubject.next(null)
    this.loaded = []
  }

  reload(domain: string | null = null, count?: number): any {
    const currentCount = this.loaded.length
    let request: LoadRequest

    this.activeDomain = domain

    // check running load requests
    if (this.subscription) {
      this.logger.log('cancelling running load')
      this.subscription.unsubscribe()
    }

    // clear the current list
    this.clear()

    // determine what to re-load
    if (count == null) {
      // simply reload everything we had (simple refresh)
      request = <LoadRequest>{
        count: currentCount,
        offset: 0,
        domain: this.activeDomain,
      }
    } else {
      // or reload count or if we had more then total count again
      request = <LoadRequest>{
        count: Math.max(count, currentCount),
        offset: 0,
        domain: this.activeDomain,
      }
    }

    return this.load(request)
  }

  abstract retrieve(request: LoadRequest): Observable<LoadResult<T>>

  private addLoadedItems(from: number, items: T[]) {
    // pad the list with null values if necessary
    if (this.loaded.length < from) {
      this.loaded.fill(null, this.loaded.length, from - 1)
    }
    // add the page to the loaded list
    // remove items if array already filled at desired index
    const toRemove = Math.min(this.loaded.length - from, items.length)
    // insert items in the array
    this.loaded.splice(from, toRemove, ...items)
  }

  private handleError(err: Error): Observable<any> {
    if (err instanceof HttpErrorResponse) {
      if (err.error && err.error.description) {
        err = new Error(err.error.description)
      } else {
        err = new Error('Error: ' + err.status + ' => ' + err.message)
      }
    }
    // log the error
    this.logger.log(err)

    // notify the service
    this.notify.error(err.message)

    return observableThrowError(new Error('Unexpected error'))
  }
}
